import { useMemo } from "react";
import { CalendarRange } from "lucide-react";
import { cn } from "@/lib/utils";
import InfoTip from "./InfoTip";
import type { ParentAnalyticsResult } from "@/hooks/useParentAnalytics";

interface Props {
  analytics: ParentAnalyticsResult;
}

const WEEKDAYS = ["M", "T", "W", "T", "F", "S", "S"];

export default function ConsistencyHeatmap({ analytics }: Props) {
  const cells = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayMonday = (() => {
      const d = new Date(today);
      const day = (d.getDay() + 6) % 7;
      d.setDate(d.getDate() - day);
      return d;
    })();

    const counts = new Map(analytics.activityCalendar.map((d) => [d.date, d.count]));
    // 4 weeks (28 days), oldest -> newest
    const out: { date: string; count: number; isFuture: boolean; label: string }[] = [];
    const start = new Date(todayMonday);
    start.setDate(start.getDate() - 21); // 4 weeks including current
    for (let i = 0; i < 28; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      out.push({
        date: key,
        count: counts.get(key) ?? 0,
        isFuture: d > today,
        label: d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }),
      });
    }
    return out;
  }, [analytics.activityCalendar]);

  // Reshape into 7 rows × 4 cols (rows = weekdays Mon..Sun)
  const grid: typeof cells[number][][] = Array.from({ length: 7 }, () => []);
  cells.forEach((c, i) => {
    grid[i % 7].push(c);
  });

  return (
    <section className="relative h-full overflow-hidden rounded-3xl border border-white/[0.06] bg-gradient-to-br from-card/80 via-card/60 to-card/40 p-5 backdrop-blur-2xl shadow-[0_24px_60px_-30px_hsl(var(--primary)/0.45),inset_0_1px_0_0_hsl(var(--foreground)/0.05)]">
      <div className="relative">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
              <CalendarRange className="h-3.5 w-3.5" />
            </span>
            <h3 className="font-heading text-sm font-semibold tracking-tight text-foreground sm:text-base">Consistency</h3>
            <InfoTip title="Consistency" body="Active learning days over the last 4 weeks. Darker squares mean more activity." />
          </div>
          <span className="text-[10px] text-muted-foreground">Last 28 Days</span>
        </div>

        <div className="mt-4 flex items-start gap-2">
          <div className="flex flex-col gap-1 pt-0.5 text-[9px] text-muted-foreground">
            {WEEKDAYS.map((d, i) => <span key={i} className="h-4 leading-4">{d}</span>)}
          </div>
          <div className="flex flex-1 flex-col gap-1">
            {grid.map((row, i) => (
              <div key={i} className="grid grid-cols-4 gap-1">
                {row.map((c) => (
                  <InfoTip
                    key={c.date}
                    title={c.label}
                    body={c.isFuture ? "Upcoming day — no activity yet." : c.count === 0 ? "No activity recorded on this day." : `${c.count} learning event${c.count === 1 ? "" : "s"} recorded.`}
                  >
                    <button
                      type="button"
                      aria-label={c.label}
                      className={cn(
                        "h-4 w-full rounded-sm transition",
                        c.isFuture ? "bg-muted/10" : heatColor(c.count),
                      )}
                    />
                  </InfoTip>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between text-[10px] text-muted-foreground">
          <span>Streak: <span className="text-foreground">{analytics.currentStreak} day{analytics.currentStreak === 1 ? "" : "s"}</span></span>
          <span className="inline-flex items-center gap-1">
            Low
            <span className="h-2 w-2 rounded-sm bg-muted/30" />
            <span className="h-2 w-2 rounded-sm bg-primary/30" />
            <span className="h-2 w-2 rounded-sm bg-primary/60" />
            <span className="h-2 w-2 rounded-sm bg-primary" />
            High
          </span>
        </div>
      </div>
    </section>
  );
}

function heatColor(count: number): string {
  if (count <= 0) return "bg-muted/25";
  if (count === 1) return "bg-primary/30";
  if (count === 2) return "bg-primary/55";
  return "bg-primary";
}